import Papa from 'papaparse'
import {
  HeaderValues,
  FileStats,
  ParseColumnType,
  CsvTemplateType,
} from './csv.constants'

// Headers are matched leniently — trimmed, case-insensitive, and tolerant of a
// leading UTF-8 BOM on the first column — to mirror how cell values are matched
// and to avoid rejecting CSVs that differ only in header casing/whitespace.
export const normalizeHeader = (header: string): string =>
  header
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()

export const transformHeader = (
  header: string,
  headers: HeaderValues[],
  fileStats: FileStats,
  seenHeaders: Set<string>,
  headerErrors: string[],
  // When true an unrecognized header is carried through as a read-only column instead of failing
  // the upload. Set only for a file whose shape legitimately exceeds what this account declares —
  // an export merged across entities carries the OTHER entities' compliance-framework columns,
  // which this account cannot know about and which only ever hold values on rows it will skip.
  tolerateUnknown = false,
): string => {
  fileStats.headersLen++
  const normalized = normalizeHeader(header)
  if (seenHeaders.has(normalized)) {
    headerErrors.push(`Header ${header} appears multiple times.`)
    return ''
  } else {
    seenHeaders.add(normalized)
  }
  const expectedHeader = headers.find((candidate) => {
    return normalizeHeader(candidate.input) === normalized
  })
  if (!expectedHeader) {
    if (tolerateUnknown) {
      return header
    }
    headerErrors.push(
      `Header name issue: '${header}' does not match expected headers.`,
    )
    return ''
  }
  return expectedHeader.output
}

// Our CSV exports put a space in front of a cell whose text would otherwise start
// with = + - @ (or a tab/CR), because Excel formula-parses those and renders e.g.
// a "- Screenshot of X" bullet as #NAME?. Take it back off here so a round-trip
// through export → import is lossless.
//
// The exact inverse of that guard, not a general trim: one space, and only when it
// shields a trigger. Since the export only ever guards a value that already starts
// with a trigger, this reverses every guard we produce and touches nothing else —
// every other importer sharing parseCSV keeps its current behavior.
export const stripFormulaGuard = (value: string): string =>
  value.replace(/^ (?=[=+\-@\t\r])/, '')

export const transformValue = (
  rawValue: string,
  columnName: string,
  fileStats: FileStats,
  columnParsers: ParseColumnType,
  bodyErrors: string[],
): string[] | string => {
  fileStats.currCell++
  const value = stripFormulaGuard(rawValue)

  if (!(columnName in columnParsers)) {
    // This a dynamically generated column if there are no header errors
    return value
  }
  const validation = columnParsers[columnName].validator(
    columnName,
    value,
    fileStats.row(),
  )
  if (validation !== null && validation.length) {
    if (Array.isArray(validation)) {
      bodyErrors.push(...validation)
      return ''
    } else {
      bodyErrors.push(validation)
      return ''
    }
  }
  return columnParsers[columnName].mutator(value)
}

export const getCSVBlob = (data: CsvTemplateType[]): Blob => {
  const csv = Papa.unparse(data)
  return new Blob([csv], { type: 'text/csv' })
}

/** Removes one pair of surrounding CSV double-quotes and un-escapes "" -> ". */
export const stripOuterCsvQuotes = (value: string): string => {
  const t = value.trim()
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1).replace(/""/g, '"')
  }
  return t
}

export const separateValues = (value: string): string[] => {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    const nextChar = value[i + 1]

    if (char === '"') {
      // Support escaped quotes in CSV fields: ""
      if (inQuotes && nextChar === '"') {
        current += '"'
        i++
        continue
      }
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && (char === ',' || char === '\n' || char === ';')) {
      const trimmed = current.trim()
      if (trimmed !== '') {
        values.push(trimmed)
      }
      current = ''
      continue
    }

    current += char
  }

  const trimmed = current.trim()
  if (trimmed !== '') {
    values.push(trimmed)
  }

  return values
}

/**
 * Escapes values that begin with risky prefixes (=, +, -, @) to prevent
 * spreadsheet formula injection.
 */
export const sanitizeCSVValue = (value: string): string => {
  const riskyPrefixes = ['=', '+', '-', '@']
  if (riskyPrefixes.some((prefix) => value.startsWith(prefix))) {
    return `'${value}`
  }
  return value
}

// Excel formula-parses any cell whose text starts with = + - @ (or a leading
// tab/CR), so a markdown bullet like "- Screenshot of X" renders as #NAME?.
// CSV quoting does NOT prevent this -- the trigger has to stop being the first
// character. A leading space is the least invasive guard that works: Excel keeps
// the space but does not evaluate the cell. stripFormulaGuard above takes it
// back off on import, which keeps our exports re-importable -- the two are
// inverses and belong together.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/

export const guardFormulaCell = (value: string): string =>
  FORMULA_TRIGGER.test(value) ? ` ${value}` : value
